import React from 'react';
import { motion } from 'framer-motion';

type GuideView = 'creations' | 'installed' | 'marketplace' | 'create';

// The Marketplace "Guide" tab: a short how-to for adding, editing, publishing and installing items.
// Keep it in step with the wizard (MyToolsPage.tsx) and the group catalog (marketplaceGroups.tsx).

const Step: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (<li className="mt-guide__step">
    <span className="mt-guide__num">{n}</span>
    <span className="mt-guide__step-text">{children}</span>
  </li>);

const Section: React.FC<{ id: string; icon: string; title: string; children: React.ReactNode }> = ({ id, icon, title, children }) => (<section className="mt-guide__section" id={id} aria-labelledby={`${id}-title`}>
    <h2 className="mt-guide__title" id={`${id}-title`}><span aria-hidden="true">{icon}</span> {title}</h2>
    {children}
  </section>);

// Screenshots live in public/images/marketplace-guide/. They show demo data (Sunrise Insurance Group);
// regenerate them when the UI they show changes.
const Shot: React.FC<{ file: string; alt: string; width: number; height: number }> = ({ file, alt, width, height }) => (<figure className="mt-guide__shot">
    <img src={`${process.env.PUBLIC_URL}/images/marketplace-guide/${file}`} alt={alt} width={width} height={height} loading="lazy" decoding="async"/>
  </figure>);

const jumpTo = (id: string) => document.getElementById(id)?.scrollIntoView({ block: 'start' });
const Jump: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (<button type="button" className="mt-guide__link" onClick={() => jumpTo(to)}>{children}</button>);

const GROUP_ROWS: [string, string][] = [
    ['Skill', "The installer's library in Digital Workforce, to turn on for any agent"],
    ['Agent', "A new agent in the installer's Digital Workforce"],
    ['MCP Server', "The installer's library in Digital Workforce, to turn on for any agent"],
    ['API Integration', "The installer's API integrations, to turn on for the agents they choose"],
    ['Widget', 'Widgets page · Right sidebar · Home tile · Toolbox (the creator picks)'],
];

const MarketplaceGuide: React.FC<{ onNavigate: (view: GuideView) => void }> = ({ onNavigate }) => {
    const link = (view: GuideView, label: string) => (<button type="button" className="mt-guide__link" onClick={() => onNavigate(view)}>{label}</button>);
    return (<motion.div className="mt-guide" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
      <div className="mt-guide__intro">
        <p>
          The Marketplace is where your agency shares skills, agents, MCP servers, widgets and API integrations. Build something once, then share it with
          your agency or every agency on Momentum AMP.
        </p>
        <nav className="mt-guide__toc" aria-label="On this page">
          <Jump to="guide-add">Add</Jump>
          <Jump to="guide-edit">Edit</Jump>
          <Jump to="guide-publish">Publish</Jump>
          <Jump to="guide-install">Install</Jump>
          <Jump to="guide-groups">Where items go</Jump>
        </nav>
      </div>

      <Section id="guide-add" icon="✨" title="Add an item">
        <ol className="mt-guide__steps">
          <Step n={1}>Open {link('create', '+ Create New')}.</Step>
          <Step n={2}><b>Type:</b> pick Skill, Agent, MCP Server, Widget or API Integration.</Step>
          <Step n={3}><b>Setup:</b> add what the type needs. That's skill content for a skill, an agent (one of yours, or a new one built right there) for an agent, a server URL for an MCP server, or a .zip / .html bundle for a widget. An API integration has its own steps: import, endpoints, info, connect and review.</Step>
          <Step n={4}><b>Info:</b> name, category, icon and visibility: <i>Personal</i> (just you), <i>My Agency</i> (your agency sees it in Browse) or <i>Global</i> (every agency, after review). A widget also chooses where it shows (see <Jump to="guide-groups">Where items go</Jump>).</Step>
          <Step n={5}><b>Store page:</b> fill in the listing page people see: a description (<b>Draft with AI</b> can write one), a video, screenshots, and the version and what's new in it.</Step>
          <Step n={6}><b>Save.</b> New items install for you automatically.</Step>
        </ol>
        <Shot file="add.webp" width={931} height={471} alt="Create New, step 1: choose Skill, Agent, Widget or MCP Server, then click Next."/>
      </Section>

      <Section id="guide-edit" icon="✏️" title="Edit an item">
        <ol className="mt-guide__steps">
          <Step n={1}>Go to {link('creations', 'My Creations')}.</Step>
          <Step n={2}>Open the item's <b>Actions</b> menu (⋯) and choose <b>Edit</b>.</Step>
          <Step n={3}>Make your changes and click <b>Update</b>.</Step>
        </ol>
        <Shot file="edit-menu.webp" width={903} height={401} alt="My Creations with an item's Actions menu open, showing Edit, Install, Share and Delete."/>
        <ul className="mt-guide__notes">
          <li>An item's type can't change. Create a new item instead.</li>
          <li>Editing a <b>Global</b> item sends it back for review. It drops to My Agency and leaves Toolbox until it's approved again. Agencies that already installed it keep it.</li>
          <li>Widget placement, version and connection changes only save when you upload the bundle again.</li>
          <li>Removing a place from a widget's "Where it's added" doesn't remove it from places it was already added.</li>
        </ul>
      </Section>

      <Section id="guide-publish" icon="🌐" title="Publish to every agency">
        <ol className="mt-guide__steps">
          <Step n={1}>Create or edit the item and set visibility to <b>Global</b>.</Step>
          <Step n={2}>Click <b>Save</b>. This sends it to Momentum AMP for review. It stays visible to you (or your agency) in the meantime.</Step>
          <Step n={3}>Track it in {link('creations', 'My Creations')}: <span className="mt-badge mt-badge--status mt-badge--pending">⏳ Pending Review</span>, then <span className="mt-badge mt-badge--status mt-badge--published">Published</span>.</Step>
          <Step n={4}>If you see <span className="mt-badge mt-badge--status mt-badge--changes">Changes Requested</span> or <span className="mt-badge mt-badge--status mt-badge--rejected">Rejected</span>, click the item to read the reviewer's feedback and reply. Fix it, then save as Global again to resubmit.</Step>
        </ol>
        <Shot file="publish-status.webp" width={905} height={474} alt="My Creations table with the Status column showing Active, Changes Requested, Pending Review and Published."/>
        <ul className="mt-guide__notes">
          <li>Once approved, every agency can find it in Browse, with your agency shown as the publisher.</li>
          <li>Each approval is a new version. People who installed it get the approved version, not your later edits, until the next approval.</li>
          <li>Widgets with <b>Toolbox</b> ticked are also listed in Toolbox.</li>
        </ul>
      </Section>

      <Section id="guide-install" icon="📦" title="Install from the Marketplace">
        <ol className="mt-guide__steps">
          <Step n={1}>Open {link('marketplace', 'Browse')} and narrow it by publisher, group or type.</Step>
          <Step n={2}>Click an item to open its store page, then <b>Install</b>. Agency Admins choose <b>Just me</b> or <b>Everyone at</b> their agency.</Step>
          <Step n={3}>Skills and MCP servers land in your library in Digital Workforce. Turn them on for any agent from that agent's Skills or Tools tab.</Step>
          <Step n={4}>Once it's installed, you can rate and review it on its store page.</Step>
          <Step n={5}>Manage or remove it anytime from {link('installed', 'Installed')}.</Step>
        </ol>
        <Shot file="browse.webp" width={907} height={401} alt="Browse with publisher, group and type filters, and item cards with Install and Install for agency buttons."/>
      </Section>

      <Section id="guide-groups" icon="🧭" title="Where items go">
        <p className="mt-guide__lead">Where each type lands when someone installs it. Only widgets have a choice; the creator picks it in the Info step.</p>
        <Shot file="where-added.webp" width={865} height={314} alt="The Where it's added options for a widget (Widgets page, Right sidebar, Home tile, Toolbox) above the Visibility choice."/>
        <div className="mt-guide__table" role="table" aria-label="Where each type can be added">
          {GROUP_ROWS.map(([type, groups]) => (<div key={type} className="mt-guide__row" role="row">
              <span className="mt-guide__cell mt-guide__cell--type" role="cell">{type}</span>
              <span className="mt-guide__cell" role="cell">{groups}</span>
            </div>))}
        </div>
        <ul className="mt-guide__notes">
          <li><b>Toolbox</b> (widgets only): listed in Toolbox once approved as Global.</li>
          <li>Workflows are coming soon.</li>
        </ul>
      </Section>

      <div className="mt-guide__cta">
        <button type="button" className="mt-btn mt-btn--primary" onClick={() => onNavigate('create')}>+ Create an item</button>
        <button type="button" className="mt-btn mt-btn--secondary" onClick={() => onNavigate('marketplace')}>Browse the Marketplace</button>
      </div>
    </motion.div>);
};

export default MarketplaceGuide;
